import { type DiscountDocument } from "@fitness-app/data-models/entities/Discount";
import { getErrorMessage } from "@fitness-app/utils";

import { getLoggedUser } from "../../../helpers/getLoggedUser";
import { getTeamOwnerId } from "../../../helpers/getTeamOwnerId";
import { type AppThunk } from "../../../index";
import {
  addDiscountToList,
  removeDiscountFromList,
  subscribeToDiscountsFailed,
  subscribeToDiscountsStarted,
  subscribeToDiscountsSuccess,
  updateDiscountOnList,
} from "../reducer";
import { fetchDiscounts } from "./fetchDiscounts";
import { unsubscribeFromDiscounts } from "./unsubscribeFromDiscounts";
import { openRealtimeChannel } from "../../../helpers/openRealtimeChannel";
import { claimRealtimeSlot, ownsRealtimeSlot } from "../../../helpers/realtimeSlot";

export const subscribeToDiscounts =
  (): AppThunk =>
  async (dispatch, getState, { auth, subscriptions, db }) => {
    const claimedSlot = claimRealtimeSlot(subscriptions, "discounts");

    try {
      dispatch(subscribeToDiscountsStarted());

      void dispatch(fetchDiscounts());

      const currentUser = await getLoggedUser(auth);
      const id = getTeamOwnerId(currentUser);

      // a newer subscribe or an unsubscribe took the slot while we were fetching
      if (!ownsRealtimeSlot(subscriptions, "discounts", claimedSlot)) {
        return;
      }

      const channelName = `discounts-list-${id}`;
      const channel = await openRealtimeChannel(db, channelName, subscriptions.realtimeDiscountsListChannel);

      // detaching the previous channel is another await - the slot may have changed hands
      if (!ownsRealtimeSlot(subscriptions, "discounts", claimedSlot)) {
        await db.removeChannel(channel);
        return;
      }

      subscriptions.discountsListChannelName = channelName;
      subscriptions.realtimeDiscountsListChannel = channel;

      subscriptions.realtimeDiscountsListChannel
        .on<DiscountDocument>(
          "postgres_changes",
          { event: "*", schema: "public", table: "discount", filter: `ownerId=eq.${id}` },
          (payload) => {
            if (payload.eventType === "UPDATE") {
              dispatch(updateDiscountOnList(payload.new));
            }
            if (payload.eventType === "INSERT") {
              dispatch(addDiscountToList(payload.new));
            }
            if (payload.eventType === "DELETE" && payload.old.id) {
              dispatch(removeDiscountFromList(payload.old.id));
            }
          },
        )
        .subscribe((status) => {
          if (status === "SUBSCRIBED") {
            dispatch(subscribeToDiscountsSuccess());
          }
          if (status === "TIMED_OUT") {
            dispatch(unsubscribeFromDiscounts());
          }
        });
    } catch (error) {
      dispatch(subscribeToDiscountsFailed(getErrorMessage(error)));
    }
  };
